/**
 * Team name lookup tables — NBA, NHL, NFL, MLB.
 *
 * Provides team code -> full name mappings used by ticker parsers
 * and game slate matching (useRotos, useSlate).
 */

export const NBA_TEAM_NAMES: Record<string, string> = {
  ATL: 'Atlanta Hawks',
  BOS: 'Boston Celtics',
  BKN: 'Brooklyn Nets',
  CHA: 'Charlotte Hornets',
  CHI: 'Chicago Bulls',
  CLE: 'Cleveland Cavaliers',
  DAL: 'Dallas Mavericks',
  DEN: 'Denver Nuggets',
  DET: 'Detroit Pistons',
  GSW: 'Golden State Warriors',
  HOU: 'Houston Rockets',
  IND: 'Indiana Pacers',
  LAC: 'Los Angeles Clippers',
  LAL: 'Los Angeles Lakers',
  MEM: 'Memphis Grizzlies',
  MIA: 'Miami Heat',
  MIL: 'Milwaukee Bucks',
  MIN: 'Minnesota Timberwolves',
  NOP: 'New Orleans Pelicans',
  NYK: 'New York Knicks',
  OKC: 'Oklahoma City Thunder',
  ORL: 'Orlando Magic',
  PHI: 'Philadelphia 76ers',
  PHX: 'Phoenix Suns',
  POR: 'Portland Trail Blazers',
  SAC: 'Sacramento Kings',
  SAS: 'San Antonio Spurs',
  TOR: 'Toronto Raptors',
  UTA: 'Utah Jazz',
  WAS: 'Washington Wizards',
};

// Kalshi packs its NHL tickers with 2-letter codes for LA / NJ / SJ / TB (`KXNHLGAME-26OCT15NJTB`), so those aliases sit
// beside the 3-letter ones (2026-10-26).
export const NHL_TEAM_NAMES: Record<string, string> = {
  ANA: 'Anaheim Ducks',
  ARI: 'Arizona Coyotes',
  BOS: 'Boston Bruins',
  BUF: 'Buffalo Sabres',
  CAR: 'Carolina Hurricanes',
  CBJ: 'Columbus Blue Jackets',
  CGY: 'Calgary Flames',
  CHI: 'Chicago Blackhawks',
  COL: 'Colorado Avalanche',
  DAL: 'Dallas Stars',
  DET: 'Detroit Red Wings',
  EDM: 'Edmonton Oilers',
  FLA: 'Florida Panthers',
  LA: 'Los Angeles Kings',
  LAK: 'Los Angeles Kings',
  MIN: 'Minnesota Wild',
  MON: 'Montreal Canadiens',
  MTL: 'Montreal Canadiens',
  NJ: 'New Jersey Devils',
  NJD: 'New Jersey Devils',
  NSH: 'Nashville Predators',
  NYI: 'New York Islanders',
  NYR: 'New York Rangers',
  OTT: 'Ottawa Senators',
  PHI: 'Philadelphia Flyers',
  PIT: 'Pittsburgh Penguins',
  SEA: 'Seattle Kraken',
  SJ: 'San Jose Sharks',
  SJS: 'San Jose Sharks',
  STL: 'St. Louis Blues',
  TB: 'Tampa Bay Lightning',
  TBL: 'Tampa Bay Lightning',
  TOR: 'Toronto Maple Leafs',
  UTA: 'Utah Hockey Club',
  VAN: 'Vancouver Canucks',
  VGK: 'Vegas Golden Knights',
  WPG: 'Winnipeg Jets',
  WSH: 'Washington Capitals',
};

/** NHL packs mix 2- and 3-letter codes (`NJTB`, `COLLA`, `SJVGK`), after an optional HHMM start: split against the
 *  registry, else the old fixed 3+3 slice. */
export function splitNhlTeams(pair: string): [string, string] | null {
  const p = pair.replace(/^\d{4}/, '');
  for (let i = 2; i <= p.length - 2; i++) {
    const a = p.slice(0, i);
    const b = p.slice(i);
    if (NHL_TEAM_NAMES[a] && NHL_TEAM_NAMES[b]) return [a, b];
  }
  if (p.length === 6) return [p.slice(0, 3), p.slice(3)];
  return null;
}

export const NFL_TEAM_NAMES: Record<string, string> = {
  ARI: 'Arizona Cardinals',
  ATL: 'Atlanta Falcons',
  BAL: 'Baltimore Ravens',
  BUF: 'Buffalo Bills',
  CAR: 'Carolina Panthers',
  CHI: 'Chicago Bears',
  CIN: 'Cincinnati Bengals',
  CLE: 'Cleveland Browns',
  DAL: 'Dallas Cowboys',
  DEN: 'Denver Broncos',
  DET: 'Detroit Lions',
  GB: 'Green Bay Packers',
  HOU: 'Houston Texans',
  IND: 'Indianapolis Colts',
  // Two spellings, one team: Kalshi packs `JAC` into its tickers, hydra stamps
  // `JAX` as the canonical code. Both are looked up here so a label renders a
  // team name whichever authority supplied the code — the parsers deliberately
  // do NOT rewrite one into the other (see parseNflGame).
  JAC: 'Jacksonville Jaguars',
  JAX: 'Jacksonville Jaguars',
  KC: 'Kansas City Chiefs',
  LAC: 'Los Angeles Chargers',
  LAR: 'Los Angeles Rams',
  LV: 'Las Vegas Raiders',
  MIA: 'Miami Dolphins',
  MIN: 'Minnesota Vikings',
  NE: 'New England Patriots',
  NO: 'New Orleans Saints',
  NYG: 'New York Giants',
  NYJ: 'New York Jets',
  PHI: 'Philadelphia Eagles',
  PIT: 'Pittsburgh Steelers',
  SEA: 'Seattle Seahawks',
  SF: 'San Francisco 49ers',
  TB: 'Tampa Bay Buccaneers',
  TEN: 'Tennessee Titans',
  WAS: 'Washington Commanders',
};

/**
 * Canonical NFL team codes. Seven are 2-char (GB, KC, LV, NE, NO, SF, TB), so a
 * packed matchup chunk runs 4–6 characters and the fixed 3+3 slice that serves
 * NBA/NHL either misreads it or rejects it outright.
 *
 * Both `JAC` and `JAX` are members deliberately (see NFL_TEAM_NAMES) — the
 * splitter returns whichever spelling the ticker packed and never rewrites one
 * into the other, or a single game would split across two grouping keys.
 */
export const NFL_TEAM_CODES: Set<string> = new Set(Object.keys(NFL_TEAM_NAMES));

/**
 * Split a concatenated NFL team pair ("NESEA", "GBPIT", "KCLV", "CLEJAC") into
 * [away, home].
 *
 * Walks every boundary and takes the split where both halves are registered.
 * The first hit is the only hit: no two registered codes concatenate into a
 * chunk that splits two ways (enumerated exhaustively in the tests), so this
 * needs no tiebreaker — which matters because a totals outcome is a bare
 * strike integer and offers nothing to break a tie with.
 *
 * A 6-char chunk that misses the registry falls back to 3+3. That is the
 * reading this parser has always given it, and an unregistered code (a
 * relocation, a new Kalshi spelling) is better off rendering an odd label than
 * dropping to `unknown`, which would hide the market behind the NFL filter —
 * the very defect this splitter exists to fix. Shorter chunks have no such
 * default and fail closed.
 */
export function splitNflTeams(pair: string): [string, string] | null {
  for (let i = 2; i <= pair.length - 2; i++) {
    const a = pair.slice(0, i);
    const b = pair.slice(i);
    if (NFL_TEAM_CODES.has(a) && NFL_TEAM_CODES.has(b)) return [a, b];
  }
  if (pair.length === 6) return [pair.slice(0, 3), pair.slice(3)];
  return null;
}

export const WNBA_TEAM_NAMES: Record<string, string> = {
  ATL: 'Atlanta Dream',
  CHI: 'Chicago Sky',
  CONN: 'Connecticut Sun',
  DAL: 'Dallas Wings',
  GS: 'Golden State Valkyries',
  IND: 'Indiana Fever',
  LA: 'Los Angeles Sparks',
  LV: 'Las Vegas Aces',
  MIN: 'Minnesota Lynx',
  NY: 'New York Liberty',
  PDX: 'Portland Fire',
  PHX: 'Phoenix Mercury',
  SEA: 'Seattle Storm',
  TOR: 'Toronto Tempo',
  WSH: 'Washington Mystics',
};

/**
 * Canonical WNBA team codes. Mixed lengths (2-char LA/LV/NY/GS, 4-char CONN)
 * mean the simple 3+3 slice used by NBA/MLB parsers doesn't work — the
 * splitter walks every split point and picks the one where both halves are
 * in this set. Mirrors hydra's WNBA registry.
 */
export const WNBA_TEAM_CODES: Set<string> = new Set(Object.keys(WNBA_TEAM_NAMES));

/** Split a concatenated WNBA team pair (e.g. "INDLA", "LVCONN") into codes. */
export function splitWnbaTeams(pair: string): [string, string] | null {
  for (let i = 2; i <= pair.length - 2; i++) {
    const a = pair.slice(0, i);
    const b = pair.slice(i);
    if (WNBA_TEAM_CODES.has(a) && WNBA_TEAM_CODES.has(b)) return [a, b];
  }
  return null;
}

export const MLB_TEAM_NAMES: Record<string, string> = {
  ARI: 'Arizona Diamondbacks',
  // Kalshi sometimes uses the 2-letter postal abbreviation on the totals
  // series (e.g. KXMLBTOTAL-26MAY132005AZTEX-3). Both forms map to the same
  // team and need to parse so the positions/trades labels render.
  AZ: 'Arizona Diamondbacks',
  ATH: 'Athletics',
  ATL: 'Atlanta Braves',
  BAL: 'Baltimore Orioles',
  BOS: 'Boston Red Sox',
  CHC: 'Chicago Cubs',
  CHW: 'Chicago White Sox',
  // Kalshi uses the postal code CWS for the White Sox on some series (game /
  // total / F5), e.g. KXMLBTOTAL-…KCCWS-9, KXMLBF5-…KCCWS-CWS — same team, both
  // forms must parse or the raw-ticker labels (Positions/Trades/TickerDisplay)
  // blank the team and the total degrades to category 'unknown'. Mirrors the
  // ARI/AZ dual-entry above. (hydra canonicalizes CWS→CHW, so store-driven
  // tables were unaffected — this is the raw-display layer only.)
  CWS: 'Chicago White Sox',
  CIN: 'Cincinnati Reds',
  CLE: 'Cleveland Guardians',
  COL: 'Colorado Rockies',
  DET: 'Detroit Tigers',
  HOU: 'Houston Astros',
  KC: 'Kansas City Royals',
  LAA: 'Los Angeles Angels',
  LAD: 'Los Angeles Dodgers',
  MIA: 'Miami Marlins',
  MIL: 'Milwaukee Brewers',
  MIN: 'Minnesota Twins',
  NYM: 'New York Mets',
  NYY: 'New York Yankees',
  OAK: 'Oakland Athletics',
  PHI: 'Philadelphia Phillies',
  PIT: 'Pittsburgh Pirates',
  SD: 'San Diego Padres',
  SEA: 'Seattle Mariners',
  SF: 'San Francisco Giants',
  STL: 'St. Louis Cardinals',
  TB: 'Tampa Bay Rays',
  TEX: 'Texas Rangers',
  TOR: 'Toronto Blue Jays',
  WSH: 'Washington Nationals',
};

/** Team name lookup by sport */
const TEAM_NAMES_BY_SPORT: Record<string, Record<string, string>> = {
  nba: NBA_TEAM_NAMES,
  wnba: WNBA_TEAM_NAMES,
  nhl: NHL_TEAM_NAMES,
  nfl: NFL_TEAM_NAMES,
  mlb: MLB_TEAM_NAMES,
};

/** Get team name from code - defaults to NBA for backwards compatibility */
export function teamNameFromCode(code: string, sport?: string): string {
  const key = String(code || '')
    .trim()
    .toUpperCase();

  // If sport specified, use that mapping (or return code for unknown sports)
  if (sport) {
    const sportKey = sport.toLowerCase();
    const mapping = TEAM_NAMES_BY_SPORT[sportKey];
    if (mapping && mapping[key]) {
      return mapping[key];
    }
    // Sport specified but no mapping - return code as-is (e.g., tennis player codes)
    return key || '\u2014';
  }

  // No sport specified: try NBA first (backwards compatibility)
  if (NBA_TEAM_NAMES[key]) {
    return NBA_TEAM_NAMES[key];
  }

  return key || '\u2014';
}

/**
 * Get team display name - full name for sports with mappings, code for others.
 * Used in sports tables to show friendly team names.
 */
export function getTeamDisplayName(code: string, sport: string): string {
  // Use full names for sports that have team name mappings
  const sportKey = sport.toLowerCase();
  if (TEAM_NAMES_BY_SPORT[sportKey]) {
    return teamNameFromCode(code, sport);
  }
  // For sports without mappings (tennis, etc.), return the code
  return code;
}
